import { isEmpty } from 'lodash-es';
import { numberToPercentage } from '../../../../utils/formatter';
import { format, addMonths } from '../../../../utils/date';
import { aiCohortsColorsMap, aiCohortsMap } from '../../configs/constants';

export const nodes = Object.keys(aiCohortsMap).reduce((acc, key) => {
  acc.push(
    { id: aiCohortsMap[key], color: aiCohortsColorsMap[key], name: aiCohortsMap[key] },
    {
      id: `End-${aiCohortsMap[key]}`,
      color: aiCohortsColorsMap[key],
      name: `End-${aiCohortsMap[key]}`,
    }
  );
  return acc;
}, []);

const tooltipPrecisionConfig = { precision: 1 };

export const segmentTransitionsConfig = ({
  selectedDate,
  legendNodeIds,
  hasData,
  activeItem,
  hoveredItem,
  onClick,
  onMouseOver,
}) => ({
  chart: {
    height: 342,
    spacing: [0, 50, 0, 50],
    marginBottom: 16,
    showAxes: true,
  },
  animation: false,
  title: {
    enabled: true,
    align: 'left',
    verticalAlign: 'bottom',
    text: hasData
      ? `
      <div class="d-flex align-items-center justify-content-between" style="width: 930px;">
        <span class="text-nowrap">${format(selectedDate, 'MMM ‘YY')}</span>
        <span class="text-nowrap">${format(addMonths(selectedDate, 1), 'MMM ‘YY')}</span>
      </div>
      `
      : '',
    style: {
      color: '#848EA1',
      fontSize: 12,
      textOverflow: 'none',
    },
    useHTML: true,
  },
  xAxis: {
    tickLength: 0,
    lineWidth: 0,
    crosshair: false,
    startOnTick: true,
    endOnTick: true,
    showFirstLabel: true,
    enabled: true,
    title: null,
    labels: {
      enabled: false,
    },
  },
  yAxis: {
    lineWidth: 0,
    tickLength: 0,
    enabled: false,
    labels: {
      enabled: false,
    },
    title: null,
  },
  legend: {
    enabled: false,
  },
  tooltip: {
    enabled: true,
    backgroundColor: 'white',
    borderRadius: 8,
    borderWidth: 0,
    hideDelay: 200,
    padding: 10,
    style: {
      opacity: 0.9,
      color: '#3b4255',
      fontSize: '12px',
    },
    shadow: false,
    followPointer: false,
    useHTML: true,
    formatter() {
      const { point, points } = this;
      if (!isEmpty(points?.[0]?.point?.options?.from)) {
        return `<p><b>${
          points[0].point.options.from
        }</b> to <b>${points[0].point.options.to.replace('End-', '')}</b>:
          ${numberToPercentage(
            points[0].point.options.weight || points[0].point.options.sum,
            tooltipPrecisionConfig
          )}</p>`;
      }
      if (point?.formatPrefix === 'point') {
        return `<p><b>${point.from}</b> to <b>${point.to.replace('End-', '')}</b>:
          ${numberToPercentage(point.weight || point.sum, tooltipPrecisionConfig)}</p>`;
      }
      if (!isEmpty(point?.options?.from) || point?.formatPrefix === 'node') {
        return `<p><b>${
          point.options.from || point.id.replace('End-', '')
        }</b>: ${numberToPercentage(
          point.options.weight || point.sum,
          tooltipPrecisionConfig
        )}</p>`;
      }
    },
    positioner(labelWidth, _, point) {
      return {
        x: labelWidth / 2,
        y: point?.plotY,
      };
    },
  },
  linkOpacity: 0.3,

  plotOptions: {
    sankey: {
      nodeWidth: 10,
      stickyTracking: false,
      states: {
        hover: {
          enabled: true,
          linkOpacity: 0.8,
          opacity: 0.8,
        },
        inactive: {
          enabled: true,
          linkOpacity: 0.8,
          opacity: 0.8,
        },
        normal: {
          enabled: true,
          linkOpacity: 0.8,
          opacity: 0.8,
        },
        selected: {
          enabled: true,
          linkOpacity: 0.8,
          opacity: 0.8,
        },
      },
      point: {
        events: {
          mouseOver() {
            if (activeItem?.id) return;
            if (this.formatPrefix === 'node' && hoveredItem?.id !== this.id) {
              onMouseOver({ id: this.id });
              this.linksTo.forEach((line) => line.update({ className: 'hovered-series' }));
              this.linksFrom.forEach((line) => line.update({ className: 'hovered-series' }));
            }
            if (this.formatPrefix === 'point' && hoveredItem?.id !== this.from) {
              onMouseOver({ id: this.from, to: this.to });
            }
          },
          click() {
            if (this.formatPrefix === 'node' && legendNodeIds.includes(this.id)) {
              onClick({ id: this.id });
            }
          },
        },
      },
      dataLabels: {
        allowOverlap: true,
        padding: 1,
        enabled: true,
        crop: false,
        overflow: 'allow',
        verticalAlign: 'middle',
        nodeAlignment: 'top',
        useHTML: true,
        zIndex: 0,
        style: {
          width: 0,
        },
        nodeFormatter() {
          const { point } = this;
          return `<div style="
            position: relative;
            height: 14px;
            z-index: 0;
            color:#363D54;
            font-size: 12px;
            font-weight: 400;
            display: block;
            width: 36px;
            height: 14px;
            ${
              point.name?.includes('End')
                ? 'left: 16px; text-align: left;'
                : 'right: 52px; text-align: right;'
            }"
            >
              ${point.sum && numberToPercentage(point.sum, tooltipPrecisionConfig)}
            </div>`;
        },
      },
    },
  },
});
